import React, { useState, useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { debounce } from "lodash";
import { useNavigate, useParams } from "react-router-dom";

import {
  BuildingOffice2Icon,
  GlobeAltIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";

import CustomButton from "../../../components/CustomButton";
import Pagination from "../../../components/CustomPagination";
import { setSearchTextByPage } from "../../../redux-store/features/providerFiltersSlice";
import { setDrawer } from "../../../redux-store/features/layoutSlice";

import SkeletonLoader from "../../../components/SkeletonLoader";
import UserDetailsDrawer from "../../../components/Drawers/UserDetailsDrawer";
import UserActionDropdown from "../../../components/UserActionDropdown";
import CustomTable from "../../../components/CustomTable"; // Import CustomTable

import {
  useGetAllSponsorUserList,
  useGetParentsponsorDetails,
  useGetSponsorUserDetails,
} from "../../../custom-hooks/queries";
import { api } from "../../../api/api-config";
import ProviderOverviewSkeleton from "../../../components/ProviderOverviewSkeletons";
import { sortConfigToSponsorUsersByOrgApiParams } from "../../../utils/sponsorUsersByOrgGridSort";
import { sortSponsorUsersByOrgItems } from "../../../utils/adminGridClientSort";
import { useAdminSortState } from "../../../custom-hooks/useAdminSortState";

const SponsorsUsersList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  // --- Parent Sponsor Details State & Fetch ---
  const [parentLoading, setParentLoading] = useState(false);
  const [parentError, setParentError] = useState(null);
  const {
    data: parentUserData,
    isLoading: detailLoading,
    isError,
  } = useGetParentsponsorDetails(id);

  // --- Local Component States ---
  const [activeTab, setActiveTab] = useState("overview");

  // Accounts tab states and queries
  const { sortConfig, setSortConfig, currentPage, setCurrentPage, handleSort } = useAdminSortState();
  const [expandedRow, setExpandedRow] = useState(null);
  const [shouldFetchDetails, setShouldFetchDetails] = useState(false);

  const searchQuery =
    useSelector(
      (state) => state.providersFilters.searchTextByPage["accountsPage"]
    ) || "";

  // Debounced search update
  const debouncedSearch = useCallback(
    debounce((search) => {
      dispatch(
        setSearchTextByPage({ pageKey: "accountsPage", searchText: search })
      );
      setCurrentPage(1);
    }, 300),
    [dispatch]
  );

  const serverSortParams = sortConfigToSponsorUsersByOrgApiParams(sortConfig);

  const { data: sponsorsList, isLoading } = useGetAllSponsorUserList({
    currentPage,
    id,
    search_text: searchQuery,
    sortOptions: serverSortParams,
  });

  const {
    data: userDetails,
    isLoading: isLoadingDetails,
    refetch: refetchUserDetails,
  } = useGetSponsorUserDetails(expandedRow, {
    enabled: Boolean(expandedRow),
  });

  useEffect(() => {
    if (expandedRow) {
      setShouldFetchDetails(true);
      refetchUserDetails();
    } else {
      setShouldFetchDetails(false);
    }
  }, [expandedRow, refetchUserDetails]);

  // --- Handlers ---
  const handleExpandRow = (rowId) => {
    if (expandedRow === rowId) {
      setExpandedRow(null);
      dispatch(setDrawer(false));
    } else {
      setExpandedRow(rowId);
      dispatch(setDrawer(true));
    }
  };

  const handleManageSponsor = (sponsorId) => {
    navigate(`/Backend/Users/ManageSponsor/${sponsorId}?sponsorName=${parentUserData?.sponsor_name}`);
  };

  const encodedId = btoa(parentUserData?.hypercomply_org_id);

  const handleAddNewAccount = () => {
    navigate(
      `/Backend/Users/AddSponsorAccount?sponsorId=${id}&sponsorName=${parentUserData?.sponsor_name}&id=${encodedId}`
    );
  };

  const formatUserDetails = (details) => {
    if (!details) return null;
    return {
      id: details.id,
      name: details.full_name,
      email: details.email,
      phone: details.phone_number || details.office_phone_number,
      first_Name: details.first_name,
      last_Name: details.last_name,
      created_Date: details.create_date_time,
      updated_Date: details.update_date_time,
      status: details.is_active ? "Active" : "Inactive",
      sponsorId: details.sponsor_id,
      contact_Preference: details.contact_preference,
    };
  };

  const getStatusBadge = (isActive) => {
    return (
      <span
        className={`inline-flex items-center rounded-md ${
          isActive
            ? "bg-green-50 text-green-700 ring-green-600/20"
            : "bg-red-50 text-red-700 ring-red-600/10"
        } px-2 py-1 text-xs font-medium ring-1 ring-inset`}
      >
        {isActive ? "Active" : "Inactive"}
      </span>
    );
  };

  const sortedItems = sortSponsorUsersByOrgItems(sponsorsList?.items, sortConfig, serverSortParams);

  // Define table columns for users
  const userColumns = [
    {
      key: "first_name",
      header: "First Name",
      sortable: true,
    },
    {
      key: "last_name",
      header: "Last Name",
      sortable: true,
    },
    {
      key: "email",
      header: "Email Address",
      sortable: true,
    },
    {
      key: "is_active",
      header: "Status",
      sortable: true,
      align: "center",
      headerClassName: "text-center",
      cellClassName: "text-center",
      render: (item) => getStatusBadge(item.is_active),
    },
    {
      key: "actions",
      header: "Actions",
      align: "right",
      render: (item) => (
        <UserActionDropdown
          onViewDetails={() => handleExpandRow(item.id)}
          onManageUser={() => handleManageSponsor(item.id)}
        />
      ),
    },
  ];

  // --- Effects ---
  // Reset sort when tab changes
  useEffect(() => {
    setSortConfig({});
  }, [activeTab]);

  // --- Render ---
  return (
    <div className="w-full min-h-screen bg-gray-50">
      {/* Top Banner */}
      <div className="flex flex-col md:flex-row items-start md:items-center justify-between px-4 py-4 bg-slate-50 shadow-sm">
        <div className="flex items-center space-x-3 mb-2 md:mb-0">
          <BuildingOffice2Icon className="h-8 w-8 text-gray-600" />
          <div>
            <h1 className="text-lg md:text-xl font-semibold text-gray-800">
              {parentUserData?.sponsor_name || "Loading..."}
            </h1>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mt-2">
        <nav className="-mb-px flex space-x-8 px-4">
          <button
            onClick={() => setActiveTab("overview")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium ${
              activeTab === "overview"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab("accounts")}
            className={`whitespace-nowrap py-4 px-1 border-b-2 text-sm font-medium ${
              activeTab === "accounts"
                ? "border-indigo-500 text-indigo-600"
                : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
            }`}
          >
            Users
          </button>
        </nav>
      </div>

      {/* Overview Tab */}
      {activeTab === "overview" && (
        <>
          {detailLoading ? (
            <div className="ml-4 mt-4">
              <ProviderOverviewSkeleton />
            </div>
          ) : (
            <div className="px-4 py-6 space-y-6">
              {/* Basic Information */}
              <div>
                <h2 className="text-base font-semibold text-gray-700 mb-2">
                  Basic Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-black">Status</p>
                    <a href="#" className="text-sm text-gray-700">
                      {getStatusBadge(parentUserData?.is_active)}
                    </a>
                    <hr className="mt-2 border-t border-gray-200" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">Website</p>
                    <a
                      href={parentUserData?.website || "#"}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-sm text-indigo-600 hover:underline"
                    >
                      {parentUserData?.website || "N/A"}
                    </a>
                    <hr className="mt-2 border-t border-gray-200" />
                  </div>
                </div>
              </div>

              {/* Location Information */}
              <div>
                <h2 className="text-base font-semibold text-gray-700 mb-2">
                  Location Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-black">Address</p>
                    <p className="text-sm text-gray-700">
                      {parentUserData?.address1} {parentUserData?.address2}
                    </p>
                    <hr className="mt-2 border-t border-gray-200" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">
                      City, State, Zip
                    </p>
                    <p className="text-sm text-gray-700">
                      {parentUserData?.city}, {parentUserData?.state}{" "}
                      {parentUserData?.zip_code}
                    </p>
                    <hr className="mt-2 border-t border-gray-200" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-black">Country</p>
                    <p className="text-sm text-gray-700">
                      {parentUserData?.country || "N/A"}
                    </p>
                    <hr className="mt-2 border-t border-gray-200" />
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}

      {/* Accounts Tab */}
      {activeTab === "accounts" && (
        <div className="px-4 py-6 mt-8 bg-white shadow-md rounded-md py-6 px-2 border border-gray-300 mx-5">
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-base font-semibold text-gray-700">
              Sponsor Accounts
            </h2>
            <CustomButton
              color="bg-indigo-600"
              size="text-base"
              weight="font-normal"
              height="h-9"
              width="w-32"
              textColor="text-white"
              text="Add User"
              borderRadius="0.375rem"
              onClick={handleAddNewAccount}
            />
          </div>

          {isLoading ? (
            <SkeletonLoader />
          ) : (
            <>
              {/* Replace the manual table with CustomTable */}
              <CustomTable
                columns={userColumns}
                data={sortedItems}
                sortConfig={sortConfig}
                onSort={handleSort}
                emptyMessage="No accounts found"
                emptyDescription="Your search did not match any accounts. Please try again."
                isSearchActive={!!searchQuery}
                edgeToEdge
              />

              {expandedRow && userDetails && (
                <UserDetailsDrawer
                  details={formatUserDetails(userDetails)}
                  isLoading={isLoadingDetails}
                  onClose={() => {
                    setExpandedRow(null);
                    dispatch(setDrawer(false));
                  }}
                />
              )}

              {!isLoading && sponsorsList?.items?.length > 0 && (
                <div className="mt-5">
                  <Pagination
                    currentPage={currentPage}
                    onPageChange={setCurrentPage}
                    totalPages={sponsorsList?.total_pages || 1}
                  />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SponsorsUsersList;
